/**
 * Back-office — Utilisateurs (super_admin). Liste unifiée de tous les comptes
 * de la plateforme, par onglet de type (clients, collectivités, partenaires,
 * techniciens, staff). Actions : détails, édition, désactivation (soft-delete
 * avec avertissement), réactivation, impersonnalisation.
 */
import type {
  ClientType,
  Permission,
  PublicUser,
  UserRole,
  UserTypeFilter,
} from '@bcl/shared';
import { clientTypeLabels, clientTypeSchema, userRoleSchema } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DoorOpen, Pencil, ShieldCheck, ShieldOff, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { IMPERSONATOR_KEY } from '@/components/impersonation-banner';
import { PermissionsChecklist } from '@/pages/admin/admin-staff-page';
import {
  deactivateUser,
  impersonateUser,
  listAllUsers,
  reactivateUser,
  updateUser,
} from '@/features/admin/admin-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { getToken } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

const tabs: { value: UserTypeFilter; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'client', label: 'Clients' },
  { value: 'collectivite', label: 'Collectivités' },
  { value: 'partenaire', label: 'Partenaires' },
  { value: 'technicien', label: 'Techniciens' },
  { value: 'staff', label: 'Staff' },
];

const roleLabels: Record<UserRole, string> = {
  super_admin: 'Super-admin',
  admin: 'Admin',
  technicien: 'Technicien',
  partenaire: 'Partenaire',
  client: 'Client',
};

// Couleur de badge par rôle (hors client) puis par type de client : chaque
// type est instantanément distinguable.
const roleBadgeClass: Record<UserRole, string> = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  technicien: 'bg-emerald-100 text-emerald-800',
  partenaire: 'bg-orange-100 text-orange-800',
  client: 'bg-slate-100 text-slate-800',
};

const clientTypeBadgeClass: Record<ClientType, string> = {
  particulier: 'bg-slate-100 text-slate-800',
  copropriete: 'bg-amber-100 text-amber-800',
  collectivite: 'bg-cyan-100 text-cyan-800',
  mairie: 'bg-sky-100 text-sky-800',
  administration: 'bg-indigo-100 text-indigo-800',
  entreprise: 'bg-teal-100 text-teal-800',
  promoteur: 'bg-violet-100 text-violet-800',
  amenageur: 'bg-lime-100 text-lime-800',
  syndic: 'bg-rose-100 text-rose-800',
  sci: 'bg-fuchsia-100 text-fuchsia-800',
  architecte: 'bg-pink-100 text-pink-800',
  bureau_etudes: 'bg-yellow-100 text-yellow-800',
};

function typeLabel(u: PublicUser): string {
  if (u.role === 'client') {
    return u.clientType ? clientTypeLabels[u.clientType] : 'Client';
  }
  return roleLabels[u.role];
}

function typeBadgeClass(u: PublicUser): string {
  if (u.role === 'client') {
    return u.clientType
      ? clientTypeBadgeClass[u.clientType]
      : 'bg-gray-100 text-gray-700';
  }
  return roleBadgeClass[u.role];
}

function homeFor(role: UserRole): string {
  switch (role) {
    case 'partenaire':
      return '/partenaire';
    case 'technicien':
      return '/technicien';
    case 'admin':
    case 'super_admin':
      return '/admin';
    default:
      return '/dashboard';
  }
}

export function AdminUsersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user: me, setSession } = useAuth();
  const [tab, setTab] = useState<UserTypeFilter>('all');
  const [search, setSearch] = useState('');

  const users = useQuery({
    queryKey: ['admin', 'users', tab],
    queryFn: () => listAllUsers(tab),
  });

  const [editing, setEditing] = useState<PublicUser | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    role: 'client' as UserRole,
    clientType: '' as '' | ClientType,
    permissions: [] as Permission[],
  });
  const [pendingDeactivate, setPendingDeactivate] = useState<PublicUser | null>(null);
  const [pendingAccess, setPendingAccess] = useState<PublicUser | null>(null);
  const [accessDraft, setAccessDraft] = useState<Permission[]>([]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] });

  function openAccess(u: PublicUser) {
    setPendingAccess(u);
    setAccessDraft(u.permissions);
  }

  const accessMut = useMutation({
    mutationFn: () =>
      updateUser(pendingAccess!.id, { permissions: accessDraft }),
    onSuccess: () => {
      toast.success('Accès mis à jour');
      setPendingAccess(null);
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function openEdit(u: PublicUser) {
    setEditing(u);
    setEditForm({
      firstName: u.firstName,
      lastName: u.lastName,
      phone: u.phone,
      role: u.role,
      clientType: u.clientType ?? '',
      // u.permissions = permissions effectives (granted) de l'admin.
      permissions: u.role === 'admin' ? u.permissions : [],
    });
  }

  const editMut = useMutation({
    mutationFn: () =>
      updateUser(editing!.id, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone,
        role: editForm.role,
        clientType:
          editForm.role === 'client' ? (editForm.clientType || null) : null,
        // Les permissions ne s'appliquent qu'au rôle admin.
        permissions: editForm.role === 'admin' ? editForm.permissions : [],
      }),
    onSuccess: () => {
      toast.success('Utilisateur mis à jour');
      setEditing(null);
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => deactivateUser(id),
    onSuccess: () => {
      toast.success('Compte désactivé');
      setPendingDeactivate(null);
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const reactivateMut = useMutation({
    mutationFn: (id: string) => reactivateUser(id),
    onSuccess: () => {
      toast.success('Compte réactivé');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  async function impersonate(u: PublicUser) {
    try {
      const myToken = getToken();
      const { token, user } = await impersonateUser(u.id);
      if (myToken && me) {
        localStorage.setItem(
          IMPERSONATOR_KEY,
          JSON.stringify({ token: myToken, user: me }),
        );
      }
      setSession(token, user);
      navigate(homeFor(user.role));
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  }

  function detailLink(u: PublicUser): string | null {
    if (u.role === 'client') return `/admin/clients/${u.id}`;
    if (u.role === 'partenaire') return `/admin/partners/${u.id}`;
    return null;
  }

  const term = search.trim().toLowerCase();
  const rows = (users.data ?? []).filter(
    (u) =>
      !term ||
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term),
  );

  return (
    <DashboardLayout variant="admin" title="Utilisateurs">
      <Card>
        <CardHeader>
          <CardTitle>Tous les utilisateurs</CardTitle>
          <CardDescription>
            {users.data ? `${rows.length} compte(s)` : 'Chargement…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-1">
            {tabs.map((t) => (
              <Button
                key={t.value}
                size="sm"
                variant={tab === t.value ? 'default' : 'outline'}
                onClick={() => setTab(t.value)}
              >
                {t.label}
              </Button>
            ))}
          </div>
          <Input
            placeholder="Rechercher (nom, email…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length ? (
                rows.map((u) => {
                  const link = detailLink(u);
                  const isSelf = u.id === me?.id;
                  return (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">
                        {u.firstName} {u.lastName}
                        {isSelf && (
                          <span className="ml-2 text-xs text-gray-500">(vous)</span>
                        )}
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={typeBadgeClass(u)}>
                          {typeLabel(u)}
                        </Badge>
                        {u.role === 'admin' && (
                          <span className="mt-1 block text-xs text-gray-500">
                            {u.permissions.length} accès
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.active ? (
                          <Badge variant="outline" className="bg-green-100 text-green-800">
                            Actif
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Désactivé</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {link && (
                            <Button asChild size="sm" variant="outline">
                              <Link to={link}>Détails</Link>
                            </Button>
                          )}
                          {u.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-blue-50 text-blue-800"
                              onClick={() => openAccess(u)}
                              aria-label="Gérer les accès"
                            >
                              <ShieldCheck className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(u)}
                            aria-label="Éditer"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {u.role !== 'super_admin' && !isSelf && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-amber-50 text-amber-800"
                              onClick={() => impersonate(u)}
                              aria-label="Se connecter en tant que cet utilisateur"
                              title="Se connecter en tant que cet utilisateur"
                            >
                              <DoorOpen className="h-4 w-4" />
                            </Button>
                          )}
                          {!isSelf &&
                            (u.active ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="bg-red-50 text-red-800"
                                onClick={() => setPendingDeactivate(u)}
                                aria-label="Désactiver"
                              >
                                <ShieldOff className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => reactivateMut.mutate(u.id)}
                                aria-label="Réactiver"
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-gray-500">
                    Aucun utilisateur.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Éditer l'utilisateur</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="edit-firstName">Prénom</Label>
                <Input
                  id="edit-firstName"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-lastName">Nom</Label>
                <Input
                  id="edit-lastName"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-phone">Téléphone</Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-role">Rôle</Label>
              <select
                id="edit-role"
                value={editForm.role}
                onChange={(e) =>
                  setEditForm({ ...editForm, role: e.target.value as UserRole })
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {userRoleSchema.options.map((r) => (
                  <option key={r} value={r}>
                    {roleLabels[r]}
                  </option>
                ))}
              </select>
            </div>
            {editForm.role === 'client' && (
              <div className="space-y-1.5">
                <Label htmlFor="edit-clientType">Type de client</Label>
                <select
                  id="edit-clientType"
                  value={editForm.clientType}
                  onChange={(e) =>
                    setEditForm({ ...editForm, clientType: e.target.value as ClientType })
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {clientTypeSchema.options.map((c) => (
                    <option key={c} value={c}>
                      {clientTypeLabels[c]}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {editForm.role === 'admin' && (
              <div className="space-y-2 rounded-md border border-dashed p-3">
                <Label>Accès de cet admin</Label>
                <p className="text-xs text-gray-500">
                  Cochez les onglets et fonctions accessibles. Deux admins
                  peuvent avoir des accès différents. Le super-admin a tout.
                </p>
                <div className="max-h-[40vh] overflow-y-auto pr-1">
                  <PermissionsChecklist
                    value={editForm.permissions}
                    onChange={(permissions) => setEditForm({ ...editForm, permissions })}
                    idPrefix="user-edit"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Annuler
            </Button>
            <Button
              disabled={!editForm.firstName || !editForm.lastName || editMut.isPending}
              onClick={() => editMut.mutate()}
            >
              {editMut.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingDeactivate !== null}
        onOpenChange={(o) => !o && setPendingDeactivate(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Désactiver ce compte ?</DialogTitle>
            <DialogDescription>
              ⚠️ Le compte
              {pendingDeactivate
                ? ` ${pendingDeactivate.firstName} ${pendingDeactivate.lastName} (${pendingDeactivate.email})`
                : ''}{' '}
              sera désactivé (suppression douce) : l'utilisateur ne pourra plus
              se connecter. Le compte et ses données sont conservés et peuvent
              être réactivés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeactivate(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={deactivateMut.isPending}
              onClick={() =>
                pendingDeactivate && deactivateMut.mutate(pendingDeactivate.id)
              }
            >
              {deactivateMut.isPending ? 'Désactivation…' : 'Désactiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingAccess !== null} onOpenChange={(o) => !o && setPendingAccess(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Accès de {pendingAccess?.firstName} {pendingAccess?.lastName}
            </DialogTitle>
            <DialogDescription>
              Cochez précisément les onglets et fonctions accessibles à cet
              admin. Deux admins peuvent avoir des accès différents.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto pr-1">
            <PermissionsChecklist
              value={accessDraft}
              onChange={setAccessDraft}
              idPrefix="user-access"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAccess(null)}>
              Annuler
            </Button>
            <Button disabled={accessMut.isPending} onClick={() => accessMut.mutate()}>
              {accessMut.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
